/**
 * @fileoverview The Gibbs action dispatcher -- the registered seam between main and the Gibbs
 * tree (proto/src/gibbs/). See docs/gibbs-migration-register.md, Slice D item 1.
 *
 * interpretActionPlan takes an ActionPlan from the text-to-action pipeline and executes a
 * single_action plan through the registry: look the ActionDef up, gate on status, validate the
 * plan, bind the declared inputs and parameters, and dispatch -- to a UserAction, a BFD server
 * command, or a job submission. Workflow, chat and clarify plans pass through for the UI.
 *
 * This module is the only file outside gibbs/ allowed to import from it
 * (test/bmaps/gibbsImportBoundary.test.js). The two imports are dynamic to avoid a load-time
 * cycle: cmds -> gibbs -> cmds. CmdInterpreter keeps a static forwarder to interpretActionPlan
 * so callers, and the fork's tests that patch it, keep the CmdInterpreter.interpretActionPlan
 * name.
 *
 * The dispatcher arrived from the fork's CmdInterpreter.js (payload 5736c4bfc) and was moved
 * here unchanged apart from the argStyle branch and this seam; see the register for what was
 * corrected and what was recorded rather than fixed (the server-command route).
 */
import { UserActions } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { Loader } from 'BMapsSrc/Loader';
import { energyErrorText, parseEnergyError } from './cmds_common';

/**
 * Log one step of a Gibbs request to the console, so what chat understood and did can be read
 * back: the request, each model answer, the plan, and the call made with its result. Live
 * objects are named, not dumped (forLog). On in every build: one line per step.
 *
 * @param {string} step - Shown after "[Gibbs] "
 * @param {Object} details
 * @param {'info'|'warn'} [level]
 */
export function logGibbs(step, details, level = 'info') {
    console[level](`[Gibbs] ${step}`, forLog(details));
}

/** Longer strings (a PDB, an SDF of every compound) are cut to this many characters in the log. */
const LOG_STRING_LIMIT = 200;

/**
 * A value as the Gibbs log shows it: plain data as it is; a live object (a compound, a protein, a
 * tree row) by its name; a long list by its first entries and its length; a long string by its
 * start and its length.
 *
 * @param {*} value
 * @param {number} [depth]
 * @returns {*}
 */
export function forLog(value, depth = 0) {
    if (typeof value === 'string' && value.length > LOG_STRING_LIMIT) {
        return `${value.slice(0, LOG_STRING_LIMIT)}… ${value.length} chars`;
    }
    if (value == null || typeof value !== 'object') return value;
    if (value instanceof Error) return value.message;
    // Deep enough for a plan's params (details → plan → actions → action → params → value).
    if (depth > 6) return '{…}';
    if (Array.isArray(value)) {
        const shown = value.slice(0, 5).map((item) => forLog(item, depth + 1));
        return value.length > 5 ? [...shown, `… ${value.length} in all`] : shown;
    }
    if (Object.getPrototypeOf(value) !== Object.prototype) return logName(value);
    return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, forLog(item, depth + 1)]),
    );
}

/** A live object's name for the log, or its class when it has none. */
function logName(object) {
    let name;
    try {
        name = object.resSpec || object.displayName || object.getName?.() || object.name;
    } catch (e) {
        name = null;
    }
    return typeof name === 'string' && name ? name : `[${object.constructor?.name || 'object'}]`;
}

/**
 * How the special values "active", "current" and "all" resolve to workspace objects, by the
 * DECLARED type of the input or parameter they were supplied for. A value supplied for anything
 * else -- a string option like export-to-format's `target: 'all'` or the hydrogen view's
 * `mode: 'all'` -- is a literal and passes through untouched. (The fork guessed from the
 * parameter's name instead, which turned `target: 'all'` into an array of case data.)
 */
export const AMBIGUOUS_PROTEIN_MESSAGE = 'Several proteins are loaded, and chat cannot yet tell'
    + ' which one you mean. For now, do this from the protein tree or the menus.';

/**
 * The protein "current" names: the one loaded. With several loaded it could be any of them, and
 * a plan cannot name one yet, so it is refused rather than guessed.
 */
function currentCaseData(ws) {
    const proteins = (ws.allCaseData?.() || []).filter((caseData) => caseData.mapCase);
    if (proteins.length > 1) throw new Error(AMBIGUOUS_PROTEIN_MESSAGE);
    return ws.firstCaseData?.() || null;
}

const SPECIAL_VALUE_RESOLVERS = {
    // A single Compound cannot be "all"; that literal stays a literal (activate-compound).
    Compound: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
    },
    CompoundList: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
        all: (ws) => ws.getLoadedCompounds?.() || [],
    },
    CaseData: {
        current: currentCaseData,
        all: (ws) => ws.allCaseData?.() || [],
    },
    'CaseData[]': {
        current: currentCaseData,
        all: (ws) => ws.allCaseData?.() || [],
    },
    // No entry for ProteinStructure: the registry declares it only on outputs.
};

/**
 * The types the table above resolves, each with its special values. The registry's
 * PLAN_SPECIAL_VALUES must match; test/gibbs/dispatchTargets.test.js checks it. Exported for
 * that test.
 */
export const SPECIAL_VALUES = Object.freeze(Object.fromEntries(
    Object.entries(SPECIAL_VALUE_RESOLVERS).map(([type, byValue]) => [type, Object.keys(byValue)])
));

/**
 * Resolve one supplied value against the ParamDef it was supplied for. Exported for its unit
 * test; the dispatcher passes App.Workspace.
 *
 * @param {*} value - the value the plan supplied
 * @param {{ name: string, type: string } | undefined} def - the declared input or parameter
 * @param {object} workspace - App.Workspace or a stand-in
 */
export function resolveSpecialValue(value, def, workspace) {
    if (typeof value !== 'string' || !def) return value;
    const resolver = SPECIAL_VALUE_RESOLVERS[def.type]?.[value];
    return resolver ? resolver(workspace) : value;
}

/** The declared inputs and parameters of an ActionDef, by name. */
function declaredParams(actionDef) {
    const byName = new Map();
    for (const def of [...actionDef.inputs, ...actionDef.parameters]) byName.set(def.name, def);
    return byName;
}

/**
 * The declared inputs that were supplied, keyed by name: the shape an `argStyle: 'options'`
 * action hands to its userAction (see gibbs/registry/schema.js). Undefined values are left out
 * so an optional input that was not given does not appear as an explicit `undefined` key.
 */
function namedInputs(actionDef, resolvedParams) {
    const named = {};
    for (const input of actionDef.inputs) {
        const value = resolvedParams[input.name];
        if (value !== undefined) named[input.name] = value;
    }
    return named;
}

function returnedErrorMessage(error) {
    // The server's JSON-encoded errors, worded as the Selector words them.
    const parsed = parseEnergyError(error);
    if (parsed?.errType) return energyErrorText(parsed);
    if (typeof error === 'string') return error;
    if (error && typeof error.message === 'string') return error.message;
    if (error && typeof error.code === 'string') return error.code;
    return 'Action reported an error.';
}

/**
 * Record one dispatch result without letting a returned failure pass as success. Main's commands
 * report failure three ways: a throw (the dispatcher catches it), a singular `error`, or a
 * non-empty `errors` array, which most server-backed commands return. The last two are reported
 * here, and a result carrying `errors` is still kept.
 */
function recordActionResult(results, entry) {
    const { result } = entry;
    if (result?.error) {
        results.errors.push(returnedErrorMessage(result.error));
        return;
    }
    if (Array.isArray(result?.errors) && result.errors.length > 0) {
        results.errors.push(...result.errors.map(returnedErrorMessage));
    }
    results.results.push(entry);
}

/**
 * Dispatch a BFD server command or search service request.
 * Handles actions that talk directly to the server without
 * a UserAction wrapper (translate-molecule, search endpoints,
 * property lookups, etc.).
 *
 * @param {string} command - Server command name
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Server response
 */
export async function dispatchServerCommand(command, params, actionDef) {
    const { ServerCmds } = await import('../server_cmds');

    // Map serverCommand names to ServerCmd objects
    const cmdMap = {
        'translate-molecule': ServerCmds.TranslateMolecule,
        'get-molecule-properties': ServerCmds.GetMoleculeProperties,
        'get-unbound-conformations': ServerCmds.GetUnboundConformations,
        'export-selection': ServerCmds.ExportSelection,
        select: ServerCmds.Select,
        'frag-data-query': ServerCmds.FragDataQuery,
    };

    const serverCmd = cmdMap[command];
    if (serverCmd) {
        return serverCmd.execute(params);
    }

    // Search/edit service endpoints (HTTP, not WebSocket)
    const { FragservWs, WebServices } = await import('../WebServices');
    if (command === 'lookupByName') {
        return FragservWs.fragLookupByName(params.names);
    }
    const serviceMap = {
        substructureSearch: {
            url: `${WebServices.FRAGSERV_HOST}/edit/substructureSearch`,
            method: 'POST',
        },
        searchEngine: {
            url: `${WebServices.FRAGSERV_HOST}/search/searchEngine`,
            method: 'POST',
        },
        getMolPic: {
            url: `${WebServices.FRAGSERV_HOST}/edit/pubpic/`,
            method: 'GET',
        },
        getProperties: {
            url: `${WebServices.FRAGSERV_HOST}/search/properties/`,
            method: 'POST',
        },
    };

    const svc = serviceMap[command];
    if (svc) {
        const response = await WebServices.startWsRequest(
            svc.url,
            svc.method === 'POST' ? JSON.stringify(params) : null
        );
        return typeof response === 'string'
            ? JSON.parse(response)
            : response;
    }

    throw new Error(
        `Unknown serverCommand: ${command}`
        + ` for action ${actionDef.id}`
    );
}

/**
 * Submit an async background job via the JobManager.
 * Handles folding, docking, scoring, REINVENT, MapLight,
 * and library screening jobs.
 *
 * @param {string} jobType - Job type from JobTypes enum
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Job submission result with jobId
 */
export async function dispatchJobSubmission(jobType, params, actionDef) {
    const { postJob } = await import('../jobman/JobsApi');

    const jobrequest = { ...params };
    const stash = {
        actionId: actionDef.id,
        actionName: actionDef.name,
    };

    const { results, error } = await postJob({
        jobtype: jobType,
        jobrequest,
        stash,
    });

    if (error) {
        throw new Error(
            `Job submission failed for ${actionDef.id}: ${error}`
        );
    }

    return {
        jobId: results.jobid,
        jobType,
        state: results.state || 'starting',
        message: `${actionDef.name} job submitted`,
    };
}

/**
 * Dispatch an ActionDef-based action plan from the Gibbs
 * text-to-action pipeline.
 *
 * For single_action plans: resolves parameters and executes
 * via UserActions, server commands, or job submission.
 * For workflow plans: returns the plan as pending
 * (for Workflow Checker UI).
 * For clarification/chat: passes through for UI rendering.
 *
 * A single action's dispatch is logged (logGibbs): the call, its arguments as bound, and the
 * result or errors.
 *
 * @param {Object} plan - ActionPlan from textToAction()
 * @returns {Promise<Object>} Execution result
 */
export async function interpretActionPlan(plan) {
    const call = {};
    const out = await dispatchPlan(plan, call);
    if (plan?.intent === 'single_action' && plan.actions?.length > 0) {
        logGibbs('dispatch', {
            actionId: plan.actions[0].actionId,
            ...call,
            results: (out.results || []).map((r) => r.result),
            errors: out.errors,
        }, out.errors?.length > 0 ? 'warn' : 'info');
    }
    return out;
}

/**
 * interpretActionPlan's work. A single action's call is written into `call` for the log:
 * `{ route, target, args }`.
 *
 * @param {Object} plan
 * @param {Object} call - Filled in as the action is dispatched
 * @returns {Promise<Object>}
 */
async function dispatchPlan(plan, call) {
    if (!plan || !plan.intent) {
        return { type: 'error', error: 'Invalid plan: missing intent' };
    }

    // Chat and clarification pass through for UI
    if (plan.intent === 'chat') {
        return { type: 'chat', message: plan.message || '' };
    }
    if (plan.intent === 'clarify') {
        return { type: 'clarification', clarification: plan };
    }

    // Workflow plans returned as pending — Workflow Checker UI handles confirmation
    if (plan.intent === 'workflow') {
        return {
            type: 'pending_workflow',
            plan,
            summary: plan.summary || 'Multi-step workflow ready for review',
        };
    }

    // Single action: execute immediately
    if (plan.intent === 'single_action' && plan.actions?.length > 0) {
        const planned = plan.actions[0];
        const results = { type: 'executed', results: [], errors: [] };

        try {
            const registry = await loadRegistry();
            const actionDef = registry.getAction(planned.actionId);
            const errors = plannedActionErrors(planned, actionDef, plan, registry);
            if (errors.length > 0) {
                results.errors.push(...errors);
                return results;
            }

            const { validatePlan } = await import('../gibbs/text-to-action/validatePlan');
            const validation = validatePlan(plan);
            if (!validation.valid) {
                results.errors.push(...validation.errors);
                return results;
            }

            return await callPlannedAction(planned, actionDef, { call });
        } catch (err) {
            results.errors.push(`Error executing ${planned.actionId}: ${err.message}`);
        }

        return results;
    }

    return { type: 'error', error: `Unhandled plan intent: ${plan.intent}` };
}

/**
 * The registry, through this seam's dynamic import: a static one closes a load-time cycle. This
 * module is the registered seam of the Gibbs import boundary
 * (test/bmaps/gibbsImportBoundary.test.js); see docs/gibbs-migration-register.md, Slice D item 1.
 */
function loadRegistry() {
    return import('../gibbs/registry');
}

/**
 * Why a planned action cannot run as planned, before anything is resolved or called; empty when
 * it can. The first half of a dispatch: a single action runs it just before its call, and a
 * workflow runs it for every step before the first one starts.
 *
 * @param {Object} planned - One planned action: `{ actionId, params, resolvedInputs }`
 * @param {Object|undefined} actionDef - Its ActionDef
 * @param {Object} plan - The plan it belongs to (a destructive action needs `plan.confirmed`)
 * @param {Object} registry - gibbs/registry
 * @returns {string[]}
 */
function plannedActionErrors(planned, actionDef, plan, registry) {
    if (!actionDef) return [`Unknown action: ${planned.actionId}`];

    if (actionDef.status !== 'implemented') {
        return [`Action "${planned.actionId}" is not implemented`
            + ` (status: ${actionDef.status || 'unspecified'}).`];
    }

    // Built, but its call does not fit this tree yet (schema.js `blockedOn`). Refused here
    // rather than misbehaving in the callee; the blocked set is pinned by
    // test/gibbs/dispatchTargets.test.js.
    if (actionDef.blockedOn) {
        return [`Action "${planned.actionId}" is not implemented on this build`
            + ` (blocked on: ${actionDef.blockedOn}).`];
    }

    // A value a plan cannot carry would reach the callee as a name or as unchecked data where it
    // needs a live object (registry types.js). A required one means the action cannot run from a
    // plan; a supplied one must be a value the plan can carry -- for a resolved type, only its
    // special values.
    const errors = [];
    const uncarried = registry.uncarriableParams(actionDef);
    for (const def of uncarried) {
        errors.push(`Action "${planned.actionId}" cannot take "${def.name}" from a plan`
            + ` (type ${def.type}, which a plan cannot carry).`);
    }
    const supplied = { ...(planned.params || {}), ...(planned.resolvedInputs || {}) };
    const defs = declaredParams(actionDef);
    for (const [key, value] of Object.entries(supplied)) {
        if (uncarried.some((def) => def.name === key)) continue;
        const problem = registry.planValueProblem(defs.get(key), value);
        if (problem) errors.push(`Action "${planned.actionId}": ${problem}.`);
        // A choice the action declares but chat cannot run yet (ParamDef unavailableOptions):
        // the planner answers it; nothing else may dispatch it.
        const unavailable = registry.unavailableOptionMessage(defs.get(key), value);
        if (unavailable) errors.push(unavailable);
    }
    if (errors.length > 0) return errors;

    // A destructive action runs only on a plan that carries an explicit confirmation. The planner
    // stages such a plan and sets `confirmed: true` only when the user confirms it
    // (gibbs/text-to-action/resumeFromClarification.js); nothing else may set it, so nothing
    // destructive dispatches unasked.
    if (actionDef.destructiveConfirm === true && plan.confirmed !== true) {
        return [`Action "${planned.actionId}" is destructive and needs confirmation:`
            + ` ${actionDef.confirmMessage}`];
    }
    return [];
}

/**
 * Resolve a planned action's values and call it. The second half of a dispatch, run only after
 * plannedActionErrors found nothing. Its special values ("active", "current", "all") are resolved
 * here, as it starts: in a workflow an earlier step may have changed what they name.
 *
 * @param {Object} planned - One planned action: `{ actionId, params, resolvedInputs }`
 * @param {Object} actionDef - Its ActionDef
 * @param {Object} [options]
 * @param {Object} [options.stepInputs] - Inputs a workflow's earlier steps produced, already live
 *   objects, by input name. Only the workflow engine supplies these, never a plan.
 * @param {Object} [options.call] - Filled in with the call made, for the log:
 *   `{ route, target, args }`
 * @returns {Promise<{type: 'executed', results: Object[], errors: string[]}>}
 */
async function callPlannedAction(planned, actionDef, { stepInputs = {}, call = {} }={}) {
    const results = { type: 'executed', results: [], errors: [] };
    try {
        // Resolve special values ("active", "current", "all") by the declared type of the
        // input or parameter each was supplied for.
        const supplied = { ...(planned.params || {}), ...(planned.resolvedInputs || {}) };
        const defs = declaredParams(actionDef);
        const resolvedParams = {};
        for (const [key, value] of Object.entries(supplied)) {
            resolvedParams[key] = resolveSpecialValue(value, defs.get(key), App.Workspace);
        }
        Object.assign(resolvedParams, stepInputs);

        // How chat says what ran: its name with the values the plan chose ("Show Hydrogen Bonds
        // (mode: none)"), and any text it returned for the user to keep (an export).
        const { valuesInWords } = await import('../gibbs/text-to-action/clarification');
        const { returnedFile } = await import('../gibbs/text-to-action/returnedFile');
        const said = valuesInWords(actionDef, supplied);
        const named = said ? `${actionDef.name} (${said})` : actionDef.name;
        const reported = (result) => ({
            actionId: planned.actionId,
            actionName: actionDef.name,
            result,
            displayText: named,
            file: returnedFile(actionDef, supplied, result),
        });

        // Required inputs are nullish-invalid. Preserve optional positional holes so a
        // missing earlier input can never re-index a later input at dispatch.
        const missingRequired = actionDef.inputs
            .filter((input) => input.required !== false
                && resolvedParams[input.name] == null)
            .map((input) => input.name);
        if (missingRequired.length > 0) {
            results.errors.push(
                `Missing required input(s) for ${planned.actionId}: ${missingRequired.join(', ')}`
            );
            return results;
        }
        const inputArgs = actionDef.inputs
            .map((input) => resolvedParams[input.name]);
        const paramObj = {};
        for (const p of actionDef.parameters) {
            if (resolvedParams[p.name] !== undefined) {
                paramObj[p.name] = resolvedParams[p.name];
            }
        }

        // Dispatch priority: userAction → serverCommand → jobType
        if (actionDef.userAction && UserActions[actionDef.userAction]) {
            // Route 1: UserAction function (most actions). The declaration is the
            // signature: inputs spread positionally in declared order, parameters as one
            // trailing options object. An ActionDef with `argStyle: 'options'` instead
            // hands its callee ONE named object holding inputs and parameters alike --
            // the shape main's named-object commands take (alignProtein(options)).
            const args = actionDef.argStyle === 'options'
                ? [{ ...namedInputs(actionDef, resolvedParams), ...paramObj }]
                : [...inputArgs];
            if (actionDef.argStyle !== 'options' && Object.keys(paramObj).length > 0) {
                args.push(paramObj);
            }
            Object.assign(call, { route: 'userAction', target: actionDef.userAction, args });
            const result = await UserActions[actionDef.userAction](...args);
            recordActionResult(results, reported(result));
        } else if (actionDef.serverCommand) {
            // Route 2: BFD server command (translate,
            // properties, search service endpoints)
            Object.assign(call, {
                route: 'serverCommand', target: actionDef.serverCommand, args: resolvedParams,
            });
            const result = await dispatchServerCommand(
                actionDef.serverCommand,
                resolvedParams,
                actionDef
            );
            recordActionResult(results, reported(result));
        } else if (actionDef.jobType) {
            // Route 3: Async job submission (folding,
            // docking, screening, REINVENT, MapLight)
            Object.assign(call, { route: 'jobType', target: actionDef.jobType, args: resolvedParams });
            const result = await dispatchJobSubmission(
                actionDef.jobType,
                resolvedParams,
                actionDef
            );
            recordActionResult(results, {
                ...reported(result), displayText: `${named} (job submitted)`,
            });
        } else {
            results.errors.push(
                `Action "${planned.actionId}" has no`
                + ' executable dispatch route (userAction:'
                + ` ${actionDef.userAction}, serverCommand:`
                + ` ${actionDef.serverCommand}, jobType:`
                + ` ${actionDef.jobType})`
            );
        }
        // (macro recording is fed from UserAction.do() — the universal
        // seam that both manual and Gibbs-dispatched actions flow
        // through — so no publish is needed here.)
    } catch (err) {
        results.errors.push(`Error executing ${planned.actionId}: ${err.message}`);
    }
    return results;
}

/**
 * Run one workflow step: the second half of a dispatch, with the inputs earlier steps produced.
 * Called by the workflow engine (gibbs/workflow/stepRunner.js). runWorkflow checked every step
 * before the first started; the step is checked again here, unconfirmed, so nothing reaches the
 * call half without the first.
 *
 * @param {Object} planned - The step: `{ actionId, params }`, its workspace references unresolved
 * @param {Object} stepInputs - Inputs earlier steps produced, by input name
 * @returns {Promise<{type: 'executed', results: Object[], errors: string[]}>}
 */
export async function runWorkflowStep(planned, stepInputs) {
    const registry = await loadRegistry();
    const actionDef = registry.getAction(planned.actionId);
    const errors = plannedActionErrors(planned, actionDef, {}, registry);
    if (errors.length > 0) return { type: 'executed', results: [], errors };
    return callPlannedAction(planned, actionDef, { stepInputs });
}

/**
 * Run a workflow plan the user has agreed to: check every step as a single action is checked,
 * then run them in order with gibbs/workflow's engine, each step's workspace references resolved
 * as it starts.
 *
 * @param {Object} plan - A workflow plan from the planner, `{ intent: 'workflow', actions }`
 * @param {Object} [callbacks] - The engine's callbacks (gibbs/workflow/executor.js), each given
 *   the step's index and node
 * @returns {Promise<{completed: boolean, errors: string[]}>}
 */
export async function runWorkflow(plan, callbacks = {}) {
    const registry = await loadRegistry();
    const errors = plan.actions.flatMap((planned) => plannedActionErrors(
        planned, registry.getAction(planned.actionId), plan, registry,
    ));
    if (errors.length > 0) return { completed: false, errors };

    const { validatePlan } = await import('../gibbs/text-to-action/validatePlan');
    const validation = validatePlan(plan);
    if (!validation.valid) return { completed: false, errors: validation.errors };

    const { createFromActionPlan } = await import('../gibbs/workflow/builders');
    const { executeWorkflow } = await import('../gibbs/workflow/executor');
    const { workflow } = createFromActionPlan(plan);
    const { completed, errors: runErrors } = await executeWorkflow(
        workflow, App.Workspace, callbacks,
    );
    return { completed, errors: runErrors };
}

/** Stop the running workflow after the step under way: that step finishes, and no other starts. */
export async function stopWorkflow() {
    const { cancelWorkflow } = await import('../gibbs/workflow/executor');
    cancelWorkflow();
}

/**
 * Plan a natural language request and run what it produces -- the chat's entry point into the
 * Gibbs tree, so a caller names neither the planner nor the workspace.
 *
 * Returns the planner's own result, with `execution` added when a plan ran:
 *  - 'chat': a message to show. `chatSource` says whose it is -- 'planner' to show as it stands,
 *    'llm' for the caller's own conversational route to answer instead.
 *  - 'clarification': a question for the user. Pass `clarification.context` and their answer to
 *    resumeInterpretText. Destructive confirmations arrive this way, as a 'destructive_confirm'
 *    cascade; the plan stays with the planner until an answer comes back. A request for several
 *    steps arrives this way too, as a 'workflow_run' proposal; answered "run",
 *    resumeInterpretText runs it.
 *  - 'plan': a single action, already dispatched. `execution` holds the result. A `message` says
 *    which action it stands in for, when it is that action's dialogFallback.
 *  - 'invalid' or 'cancelled': nothing ran.
 *
 * @param {string} text - What the user asked for
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Object>}
 */
export async function interpretText(text, { signal }={}) {
    logGibbs('request', { text });
    const { textToAction } = await import('../gibbs/text-to-action');
    const result = await textToAction(text, App.Workspace, { signal, campaignsEnabled });
    logPlannerAnswer(result);
    if (result.type !== 'plan') return result;
    return { ...result, execution: await interpretActionPlan(result.plan) };
}

/** Whether campaigns are on: the deputy's setting, as the Workflow pane reads it. */
async function campaignsEnabled() {
    const { isDeputyEnabled } = await import('../deputy');
    return isDeputyEnabled({ enabled: Loader.AutonomousCampaignEnabled });
}

/** Log what the planner answered: its type, and the plan, question or message it carries. */
function logPlannerAnswer(result) {
    const {
        type, tier, chatSource, plan, clarification, message, errors, retryAttempts,
    } = result;
    logGibbs(`answer: ${type}`, {
        tier,
        chatSource,
        retryAttempts,
        plan,
        question: clarification?.question,
        options: clarification?.options,
        message,
        errors,
    });
}

/**
 * Answer an outstanding clarification and run the plan it completes, if it completes one. The
 * result shapes are interpretText's: another 'clarification' when more is needed, a dispatched
 * 'plan', or 'chat' carrying what the planner had to say instead -- a cancelled confirmation, or
 * one no longer pending.
 *
 * A workflow proposal answered "run" runs the workflow here, as 'workflow_run': `execution` holds
 * `{ completed, errors }`, and `onStep` hears each step as it starts and ends.
 *
 * @param {Object} context - `clarification.context` from an earlier interpretText
 * @param {{value: *}|{input: string}} answer - `{ value }` with the chosen option's value (a
 *   confirmation's or a proposal's options carry `{decision}`), or `{ input }` with text typed
 *   into a typed-answer option
 * @param {Object} [options]
 * @param {function({index: number, total: number, name: string,
 *   status: 'started'|'done'|'failed', said?: string, error?: string, summary?: string|null,
 *   file?: {text: string, name: string}|null, warnings?: string[]}): void}
 *   [options.onStep] Each workflow step, by its action's name; a starting step's `said` gives
 *   the values it runs with, and a finished step's `summary` says what it produced
 *   (gibbs/workflow/stepSummary) and its `file` any text it returned (an export)
 * @returns {Promise<Object>}
 */
export async function resumeInterpretText(context, answer, { onStep } = {}) {
    const { resumeFromClarification } = await import(
        '../gibbs/text-to-action/resumeFromClarification'
    );
    logGibbs('resume', { context, answer });
    const {
        plan, needsMoreClarification, chatMessage, review,
    } = resumeFromClarification(context, answer, App.Workspace);
    let message = chatMessage;
    if (review) {
        // The pane would put it in place of a plan it is running, so nothing opens during a run.
        const { isWorkflowRunning } = await import('../gibbs/workflow/executor');
        if (isWorkflowRunning()) {
            message = 'A workflow is running, so nothing was opened. Ask again when it finishes.';
        } else {
            // The Workflow pane takes it from here (WorkflowPane's handleProposed).
            EventBroker.publish('workflowProposed', review);
        }
    }
    let result;
    if (needsMoreClarification) {
        result = { type: 'clarification', clarification: needsMoreClarification };
    } else if (plan) {
        result = { type: 'plan', plan };
    } else {
        result = { type: 'chat', message: message || '', chatSource: 'planner' };
    }
    logPlannerAnswer(result);
    if (plan?.intent === 'workflow') {
        const callbacks = await stepEvents(plan, onStep);
        return { type: 'workflow_run', plan, execution: await runWorkflow(plan, callbacks) };
    }
    if (result.type !== 'plan') return result;
    return { ...result, execution: await interpretActionPlan(plan) };
}

/**
 * The engine's callbacks for a workflow, reporting each step to `onStep` by its action's name.
 *
 * @param {Object} plan - The workflow plan
 * @param {Function} [onStep] - See resumeInterpretText
 * @returns {Promise<Object>} executeWorkflow's callbacks
 */
async function stepEvents(plan, onStep) {
    if (!onStep) return {};
    const { getAction } = await loadRegistry();
    const { stepSummary } = await import('../gibbs/workflow/stepSummary');
    const { valuesInWords } = await import('../gibbs/text-to-action/clarification');
    const total = plan.actions.length;
    const step = (index, node, status, extra) => onStep({
        index, total, name: getAction(node.actionId)?.name || node.actionId, status, ...extra,
    });
    return {
        // The values the step runs with, as a single action's "Done:" says them.
        onStepStart: (index, node) => {
            const def = getAction(node.actionId);
            step(index, node, 'started', { said: def ? valuesInWords(def, node.paramOverrides) : '' });
        },
        onStepComplete: (index, node, stepResult) => step(index, node, 'done', {
            // stepResult.result is runWorkflowStep's: its first result is the command's return,
            // with any text it returned for the user to keep.
            summary: stepSummary(getAction(node.actionId), stepResult?.outputs,
                stepResult?.result?.results?.[0]?.result),
            file: stepResult?.result?.results?.[0]?.file || null,
            warnings: stepResult?.warnings || [],
        }),
        onStepFailed: (index, node, error) => step(index, node, 'failed', { error }),
    };
}
